import React, { useCallback, useEffect, useRef, useState } from "react";
import { SYM, withEllipsis } from "../utils/textSymbols";
import "./AssistantPanel.css";

function TypeBadge({ type }) {
  const labels = { calendar: "Calendar", grocery: "Grocery", todo: "To-Do" };
  return <span className="assistant-proposal-type">{labels[type] || type}</span>;
}

export default function AssistantPanel({
  apiBase,
  open,
  onClose,
  onOpenApproval,
  onRefreshBell
}) {
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [previousResponseId, setPreviousResponseId] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToEnd = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBase}/api/AssistantChat`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setMessages(Array.isArray(data.messages) ? data.messages : []);
        setConversationId(data.conversationId || null);
        setPreviousResponseId(data.previousResponseId || null);
      } catch {
        /* keep local state */
      } finally {
        if (!cancelled) {
          setLoaded(true);
          window.setTimeout(scrollToEnd, 0);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, apiBase, scrollToEnd]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (open && loaded) {
      window.setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, loaded]);

  useEffect(() => {
    scrollToEnd();
  }, [messages, busy, scrollToEnd]);

  const send = async (text) => {
    const content = String(text || "").trim();
    if (!content || busy) return;
    setError(null);
    setDraft("");
    const optimistic = {
      id: `local-${Date.now()}`,
      role: "user",
      content,
      proposals: [],
      at: new Date().toISOString()
    };
    setMessages((prev) => [...prev, optimistic]);
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/AssistantChat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: content, conversationId, previousResponseId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Assistant error ${res.status}`);
      }
      if (Array.isArray(data.messages) && data.messages.length) {
        setMessages(data.messages);
      } else {
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== optimistic.id),
          optimistic,
          {
            id: `asst-${Date.now()}`,
            role: "assistant",
            content: data.reply || "",
            proposals: data.proposals || [],
            at: new Date().toISOString()
          }
        ]);
      }
      setConversationId(data.conversationId || conversationId);
      setPreviousResponseId(data.previousResponseId || null);
      if (Array.isArray(data.proposals) && data.proposals.length && onRefreshBell) {
        onRefreshBell();
      }
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setDraft(content);
      setError(err.message || "Could not reach the assistant.");
    } finally {
      setBusy(false);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    send(draft);
  };

  const handleNewChat = async () => {
    if (busy) return;
    try {
      await fetch(`${apiBase}/api/AssistantChat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reset: true })
      });
    } catch {
      /* still reset locally */
    }
    setMessages([]);
    setConversationId(null);
    setPreviousResponseId(null);
    setError(null);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  if (!open) return null;

  return (
    <div className="assistant-panel-overlay" onClick={onClose}>
      <aside className="assistant-panel" onClick={(e) => e.stopPropagation()} aria-label="Family assistant">
        <header className="assistant-panel-header">
          <h2>Assistant</h2>
          <div className="assistant-panel-header-actions">
            <button type="button" className="assistant-new-btn" onClick={handleNewChat} disabled={busy}>
              New chat
            </button>
            <button type="button" className="assistant-panel-close" onClick={onClose} aria-label="Close">
              {SYM.close}
            </button>
          </div>
        </header>

        {error && <p className="assistant-panel-error">{error}</p>}

        <div className="assistant-panel-body" ref={listRef}>
          {messages.length === 0 && !busy && (
            <p className="assistant-panel-empty">
              Ask about the calendar, groceries, meals, or to-dos. I can also propose new items for you to approve.
            </p>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className={`assistant-msg assistant-msg-${msg.role}`}>
              <p className="assistant-msg-body">{msg.content}</p>
              {Array.isArray(msg.proposals) && msg.proposals.length > 0 && (
                <div className="assistant-proposals">
                  {msg.proposals.map((p) => (
                    <div key={p.requestId} className="assistant-proposal-card">
                      <div className="assistant-proposal-main">
                        <TypeBadge type={p.type} />
                        <span className="assistant-proposal-id">{p.requestId}</span>
                        {p.summary && <span className="assistant-proposal-summary">{p.summary}</span>}
                      </div>
                      <button
                        type="button"
                        className="assistant-review-btn"
                        onClick={() => onOpenApproval?.(p.requestId)}
                      >
                        Review in Approvals
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {busy && <p className="assistant-panel-thinking">{withEllipsis("Looking things up")}</p>}
        </div>

        <form className="assistant-composer" onSubmit={handleSubmit}>
          <textarea
            ref={inputRef}
            className="assistant-composer-input"
            rows={2}
            value={draft}
            disabled={busy}
            placeholder="Ask about the week, or offer something to add"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button type="submit" className="assistant-send-btn" disabled={busy || !draft.trim()}>
            Send
          </button>
        </form>
      </aside>
    </div>
  );
}
